/**
 * Client-side password hashing:
 * 1. SHA256(password) only - no client-side salting
 * 2. Send to server over HTTPS for bcrypt with per-user salt + AES encryption with global pepper
 */
export class AuthUtils {
  /**
   * Hash password client-side using SHA256 only
   */
  static async hashPassword(password: string): Promise<string> {
    // Simple SHA256 of password only
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  /**
   * Complete client-side password processing for registration/login
   * Returns SHA256 hash to be sent over HTTPS to server
   */
  static async processPassword(password: string): Promise<string> {
    return await this.hashPassword(password);
  }

  /**
   * Validate password strength
   */
  static validatePassword(password: string): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Heslo musí mít alespoň 8 znaků');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Heslo musí obsahovat alespoň jedno velké písmeno');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Heslo musí obsahovat alespoň jedno malé písmeno');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Heslo musí obsahovat alespoň jednu číslici');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Heslo musí obsahovat alespoň jeden speciální znak');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate email format
   */
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Sanitize username (remove special chars, preserve case)
   */
  static sanitizeUsername(username: string): string {
    return username
      .replace(/[^a-zA-Z0-9._-]/g, '')
      .substring(0, 50); // Max length 50
  }
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterCredentials {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user?: {
    id: string;
    username: string;
    email: string;
  };
}